/**
 * What the welcome card (components/WelcomeModal) offers after a sign-up:
 * the button's words for the page the person came from, and three paths
 * to take from here.
 *
 * Three, not more: every extra option at one decision slows the first tap,
 * and the first-run screens of the apps we checked on Mobbin mostly offer
 * one to four (2026-10-31). The paths follow the goal the person picked
 * (the hub's goal picker, or a visitor's pick the new account inherits);
 * the flow they came from is the button's job, so it is never a path too.
 * Everything else stays one tap away in the menu and the hub's roadmap.
 */
import type { TranslationKey } from "@/lib/i18n";

export type PathKind = "analyze" | "map" | "alerts" | "calculate" | "zones" | "listings" | "chat";

/** Where each path goes. */
export const PATH_HREF: Record<PathKind, string> = {
  analyze: "/analyze",
  map: "/map",
  alerts: "/alerts",
  calculate: "/calculate",
  zones: "/map/zones",
  listings: "/listings",
  chat: "/chat",
};

/** The button's words for a destination path. */
export function destinationKey(path: string | null | undefined): TranslationKey {
  const p = (path ?? "/").replace(/^\/(az|ru|en)(?=\/|$)/, "") || "/";
  if (p.startsWith("/map/zones") || p.startsWith("/calculate/sale-tax")) return "welcome.go.zones";
  if (p.startsWith("/map")) return "welcome.go.map";
  if (p.startsWith("/alerts")) return "welcome.go.alerts";
  if (p.startsWith("/analyze") || p.startsWith("/r/") || p.startsWith("/history")) return "welcome.go.analyze";
  if (p.startsWith("/calculate")) return "welcome.go.calculate";
  if (p.startsWith("/listings") || p.startsWith("/elan/") || p.startsWith("/l/")) return "welcome.go.listings";
  if (p.startsWith("/chat")) return "welcome.go.chat";
  if (p.startsWith("/listing-analytics")) return "welcome.go.analytics";
  if (p.startsWith("/watchlist")) return "welcome.go.watchlist";
  if (p.startsWith("/residence")) return "welcome.go.residence";
  if (p.startsWith("/link-telegram")) return "welcome.go.telegram";
  if (p.startsWith("/properties")) return "welcome.go.properties";
  if (p.startsWith("/find")) return "welcome.go.find";
  return "welcome.go.home";
}

/** The path a "back to it" button already covers. */
const GO_TO_PATH: Partial<Record<TranslationKey, PathKind>> = {
  "welcome.go.zones": "zones",
  "welcome.go.map": "map",
  "welcome.go.alerts": "alerts",
  "welcome.go.analyze": "analyze",
  "welcome.go.calculate": "calculate",
  "welcome.go.listings": "listings",
  "welcome.go.chat": "chat",
};

/** Per goal, in order (lib/use-personal Intent; "none" without one). A
 *  buyer or an investor checks a price first; a renter, an office or a
 *  garage seeker searches first; a seller and a landlord value their own
 *  place first. */
const BY_GOAL: Record<string, PathKind[]> = {
  none: ["analyze", "alerts", "map"],
  buy: ["analyze", "alerts", "map"],
  invest: ["analyze", "map", "alerts"],
  rent: ["listings", "alerts", "analyze"],
  sell: ["calculate", "zones", "map"],
  rent_out: ["calculate", "listings", "map"],
  agent: ["analyze", "calculate", "map"],
  commercial: ["listings", "alerts", "map"],
  garage: ["listings", "alerts", "map"],
};

/** Fills the list when the button takes one of the goal's paths. */
const FALLBACK: PathKind[] = ["analyze", "alerts", "map", "listings", "calculate", "zones", "chat"];

/** The three paths for the flow the person came from and their goal. */
export function pathsFor(goKey: TranslationKey, goal?: string | null): PathKind[] {
  const own = GO_TO_PATH[goKey];
  const plan = (goal && Object.prototype.hasOwnProperty.call(BY_GOAL, goal) ? BY_GOAL[goal] : BY_GOAL.none);
  const out: PathKind[] = [];
  for (const k of [...plan, ...FALLBACK]) {
    if (out.length >= 3) break;
    if (k !== own && !out.includes(k)) out.push(k);
  }
  return out;
}
